import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import type { Enums } from "@/integrations/supabase/types";

export type InboxKind =
  | "lead_no_response"
  | "budget_draft"
  | "budget_stalled"
  | "project_overdue"
  | "project_review_stuck";

export type InboxSeverity = "low" | "medium" | "high";

export interface InboxItem {
  kind: InboxKind;
  ref_id: string;
  title: string;
  subtitle: string;
  severity: InboxSeverity;
  since: string;
  link: string;
  client_id: string | null;
}

export function useInbox() {
  return useQuery({
    queryKey: ["admin-inbox"],
    queryFn: async () => {
      const { data, error } = await supabase.from("v_admin_inbox" as never).select("*");
      if (error) throw error;
      const items = (data ?? []) as unknown as InboxItem[];
      const severityRank: Record<InboxSeverity, number> = { high: 0, medium: 1, low: 2 };
      return items.sort((a, b) => {
        const s = severityRank[a.severity] - severityRank[b.severity];
        if (s !== 0) return s;
        return new Date(a.since).getTime() - new Date(b.since).getTime();
      });
    },
    refetchInterval: 60_000,
  });
}

export interface ActivityItem {
  id: string;
  kind: "lead" | "budget" | "project" | "post";
  title: string;
  subtitle: string;
  at: string;
  link: string;
}

type LeadActivityRow = {
  id: string;
  name: string;
  status: Enums<"lead_status"> | null;
  created_at: string | null;
};
type BudgetActivityRow = {
  id: string;
  title: string;
  status: string | null;
  updated_at: string | null;
  lead_id: string | null;
};
type ProjectActivityRow = {
  id: string;
  title: string;
  phase: Enums<"project_phase"> | null;
  updated_at: string | null;
  lead_id: string | null;
};
type PostActivityRow = {
  id: string;
  title: string;
  published: boolean | null;
  created_at: string | null;
};

export function useRecentActivity(limit = 12) {
  return useQuery({
    queryKey: ["admin-activity", limit],
    queryFn: async () => {
      const [leads, budgets, projects, posts] = await Promise.all([
        supabase
          .from("leads" as never)
          .select("id, name, status, created_at")
          .is("deleted_at", null)
          .order("created_at", { ascending: false })
          .limit(limit),
        supabase
          .from("budgets" as never)
          .select("id, title, status, updated_at, lead_id")
          .order("updated_at", { ascending: false })
          .limit(limit),
        supabase
          .from("client_projects")
          .select("id, title, phase, updated_at, lead_id")
          .order("updated_at", { ascending: false })
          .limit(limit),
        supabase
          .from("blog_posts" as never)
          .select("id, title, published, created_at")
          .order("created_at", { ascending: false })
          .limit(limit),
      ]);

      const items: ActivityItem[] = [];
      ((leads.data ?? []) as LeadActivityRow[]).forEach((l) =>
        items.push({
          id: `lead-${l.id}`,
          kind: "lead",
          title: l.name,
          subtitle: `Lead • ${l.status}`,
          at: l.created_at ?? "",
          link: `/admin/leads/${l.id}`,
        }),
      );
      ((budgets.data ?? []) as BudgetActivityRow[]).forEach((b) =>
        items.push({
          id: `budget-${b.id}`,
          kind: "budget",
          title: b.title,
          subtitle: `Orçamento • ${b.status}`,
          at: b.updated_at ?? "",
          link: b.lead_id ? `/admin/leads/${b.lead_id}?tab=proposta` : "/admin/pipeline",
        }),
      );
      ((projects.data ?? []) as ProjectActivityRow[]).forEach((p) =>
        items.push({
          id: `project-${p.id}`,
          kind: "project",
          title: p.title,
          subtitle: `Projeto • ${p.phase}`,
          at: p.updated_at ?? "",
          link: p.lead_id ? `/admin/leads/${p.lead_id}?tab=entrega` : "/admin/pipeline",
        }),
      );
      ((posts.data ?? []) as PostActivityRow[]).forEach((p) =>
        items.push({
          id: `post-${p.id}`,
          kind: "post",
          title: p.title,
          subtitle: `Post • ${p.published ? "publicado" : "rascunho"}`,
          at: p.created_at ?? "",
          link: `/admin/blog/edit/${p.id}`,
        }),
      );
      return items
        .sort((a, b) => new Date(b.at).getTime() - new Date(a.at).getTime())
        .slice(0, limit);
    },
  });
}
